import { Capacitor } from '@capacitor/core';

/**
 * Runtime platform detection for the React app.
 *
 * The same Vite build is shipped both as a web app (Azure SWA / App Service)
 * and as the static assets inside a Capacitor iOS / Android shell. These
 * helpers let components branch on which surface they're running on without
 * checking user-agents or peppering Capacitor imports through the codebase.
 *
 * `isNativeApp()` is true inside the iOS / Android shells and false in the
 * web browser (including the PWA "Add to Home Screen" install — that's still
 * Safari/Chrome under the hood, not a Capacitor WebView).
 */
export const isNativeApp = (): boolean => Capacitor.isNativePlatform();

/** Discriminate between iOS and Android when we need platform-specific UX. */
export const getNativePlatform = (): 'ios' | 'android' | 'web' => {
  const p = Capacitor.getPlatform();
  return p === 'ios' || p === 'android' ? p : 'web';
};

/**
 * Which native shell this build was produced for. The same SPA ships inside
 * two Capacitor apps — Dispatch (field workers) and the Customer Portal —
 * distinguished at build time by VITE_NATIVE_APP ('portal' via the
 * build:portal script; anything else, including web builds, means 'field').
 * Only meaningful when isNativeApp() is true.
 */
export type NativeAppMode = 'field' | 'portal';
export const nativeAppMode = (): NativeAppMode =>
  import.meta.env?.VITE_NATIVE_APP === 'portal' ? 'portal' : 'field';
